import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Tooltip } from "@blueprintjs/core";
import { Field, useField, useForm } from "react-final-form";
import { isEqual } from "lodash";
import InfoIconWithTooltip from "components/Tooltips/InfoIconWithTooltip";
import TextTruncate from "components/TextTruncate";
import { getRestrictionsValidator, isNotEmpty } from "utils/validate";
import { getDefaultValue } from "./utils";
import { usePrevious } from "hooks/usePrevious";
import * as styles from "./styles.module.scss";
export var FieldStatus;
(function (FieldStatus) {
    FieldStatus["VALID"] = "valid";
    FieldStatus["EMPTY"] = "empty";
    FieldStatus["FOCUSED"] = "focused";
    FieldStatus["ERROR"] = "error";
})(FieldStatus || (FieldStatus = {}));
const Input = ({ component: Component, value: customValue, name, label, errorLabel, readonly, classes = {}, componentClasses, infoIconText, learnMoreUrl, restrictions, errorInTooltip = false, customValidator, suppressErrors = false, externalError, suppressStateClass, showErrorIfUntouched, onError, valueFormatter, disableFormChange, onChange, defaultLastValidValue, ...rest }) => {
    const { input, meta } = useField(name);
    const form = useForm();
    const [lastValidValue, setLastValidValue] = useState(defaultLastValidValue);
    const isEmptyValue = useMemo(() => !isNotEmpty(input.value, true), [input.value]);
    const error = useMemo(() => externalError || (meta.touched || showErrorIfUntouched ? meta.error : ""), [externalError, meta.error, meta.touched, showErrorIfUntouched]);
    const previousRestrictions = usePrevious(restrictions);
    const defaultValue = useMemo(() => getDefaultValue({ Default: restrictions?.Default }), [restrictions?.Default]);
    const stateClass = useMemo(() => {
        if (suppressStateClass) {
            return FieldStatus.VALID;
        }
        else if (meta.active) {
            return FieldStatus.FOCUSED;
        }
        else {
            if (!error) {
                if (restrictions?.IsMandatory || restrictions?.IsNecessary) {
                    return isEmptyValue ? FieldStatus.EMPTY : FieldStatus.VALID;
                }
                else {
                    return FieldStatus.VALID;
                }
            }
            else {
                return FieldStatus.ERROR;
            }
        }
    }, [
        error,
        isEmptyValue,
        meta.active,
        restrictions?.IsMandatory,
        restrictions?.IsNecessary,
        suppressStateClass,
    ]);
    const value = useMemo(() => (customValue !== undefined ? customValue : input.value), [customValue, input.value]);
    const handleChange = useCallback((value) => {
        const valueToSave = valueFormatter && value !== undefined ? valueFormatter(value) : value;
        const prevValue = input.value;
        if (!disableFormChange) {
            input.onChange(valueToSave);
        }
        if (onChange) {
            onChange(valueToSave, prevValue);
        }
    }, [disableFormChange, input, onChange, valueFormatter]);
    const onFocus = useCallback(() => {
        if (!isEmptyValue && meta.valid) {
            setLastValidValue(input.value);
        }
        input.onFocus();
    }, [input, isEmptyValue, meta.valid]);
    const validate = useCallback((value, allValues, meta) => {
        const validator = getRestrictionsValidator(restrictions, errorLabel ?? label ?? name, customValidator);
        return validator(value, allValues, meta);
    }, [customValidator, errorLabel, label, name, restrictions]);
    const renderError = useCallback((fieldActive) => {
        if (!!error && !fieldActive && !suppressErrors) {
            if (errorInTooltip) {
                return (React.createElement(Tooltip, { popoverClassName: styles["tooltip-portal"], isOpen: true, placement: "bottom", usePortal: false, content: error, targetTagName: "div" },
                    React.createElement("div", { className: styles["error-tooltipTarget"] })));
            }
            else {
                return React.createElement("div", { className: classNames(styles["error-message"], classes.error) }, error);
            }
        }
    }, [classes.error, error, errorInTooltip, suppressErrors]);
    useEffect(() => {
        if (!meta.active && isEmptyValue && isNotEmpty(lastValidValue, true) && !!restrictions?.IsMandatory) {
            input.onChange(lastValidValue);
            input.onBlur(); // validate
        }
    }, [input, meta, isEmptyValue, lastValidValue, restrictions?.IsMandatory]);
    useEffect(() => {
        if (!isEqual(restrictions, previousRestrictions)) {
            form.resetFieldState(input.name);
        }
    }, [form, input.name, previousRestrictions, restrictions]);
    useEffect(() => {
        if (
        /**/
        //meta.pristine - Set default value if field does not changed
        //(!meta.pristine && !meta.touched && isNotEmpty(meta.initial))
        // (!meta.active) - set default value only when field is inactive. on focus out
        //Set default value if form ever render this field previously, but it was removed by form.change
        //resetNestedFieldsTouchedMutator should be used
        /**/
        (meta.pristine || (!meta.pristine && !meta.touched && isNotEmpty(meta.initial))) &&
            isEmptyValue &&
            defaultValue !== undefined &&
            !meta.active) {
            input.onChange(defaultValue);
            input.onBlur(); // validate
            setLastValidValue(defaultLastValidValue ?? defaultValue);
        }
    }, [
        defaultLastValidValue,
        defaultValue,
        input,
        isEmptyValue,
        meta.active,
        meta.initial,
        meta.pristine,
        meta.touched,
    ]);
    useEffect(() => {
        if (meta.touched && !meta.active && onError) {
            onError(stateClass === FieldStatus.ERROR || stateClass === FieldStatus.EMPTY);
        }
    }, [input.value, meta.active, meta.touched, meta.valid, onError, stateClass]);
    return (React.createElement(Field, { name: name, validate: validate }, ({ input, meta }) => (React.createElement("div", { role: "input", className: classNames(styles["input-root"], classes.container, {
            [styles[stateClass]]: !readonly,
        }) },
        label && (React.createElement("p", { role: "label", className: classNames(styles["label-wrapper"], { [styles["with-tooltip"]]: !!infoIconText }) },
            React.createElement(TextTruncate, { className: classNames(styles.label, classes.label), text: label }),
            restrictions?.IsMandatory === true ? React.createElement("span", { className: styles.asterisk }, "*") : null)),
        !!infoIconText && (React.createElement(InfoIconWithTooltip, { classes: { className: classNames(styles["info-tooltip"], classes.infoTooltip) }, tooltipText: infoIconText, learnMoreUrl: learnMoreUrl })),
        React.createElement(Component, { ...input, ...rest, onChange: handleChange, onBlur: (e) => {
                input.onBlur(e);
                if (rest.onBlur) {
                    rest.onBlur(input, e);
                }
            }, onFocus: onFocus, value: value, name: name, className: classNames(styles.input, classes.component), readonly: readonly, ...(componentClasses !== undefined ? { componentClasses } : {}), ...(defaultValue !== undefined ? { placeholder: lastValidValue ?? defaultValue } : {}), ...(restrictions?.MaxLength !== undefined ? { maxLabelLength: restrictions.MaxLength } : {}) }),
        renderError(!!meta.active)))));
};
export default Input;
